import React, { useState, useEffect } from 'react';

import { CheckCircleOutlined, MailOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { Card, Form, Input, Button, message, Spin, Alert, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { handleApiError } from '../../utils/apiErrorHandler';

const OperatorInvitationAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/public/v1/operator_invitations/${token}`);
      const data = await response.json();

      if (data.success && data.data) {
        setInvitation(data.data);
        form.setFieldsValue({
          email: data.data.email,
          username: data.data.username
        });
      } else {
        setError(data.message || t('public.invitation.error.fetch_failed'));
      }
    } catch (err) {
      console.error('Failed to fetch invitation:', err);
      setError(t('public.invitation.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const response = await fetch(`/api/public/v1/operator_invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (data.success) {
        message.success(data.message || t('public.invitation.messages.accept_success'));
        setSuccess(true);
        // 3 秒後跳轉到 Operator 登入頁面
        setTimeout(() => {
          window.location.href = '/operator/login';
        }, 3000);
      } else {
        // 由 handleApiError 抓出巢狀 errors.base + interpolations 並翻譯
        handleApiError(data, { t, fallbackMessage: t('public.invitation.error.accept_failed') });
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      message.error(t('public.invitation.error.accept_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5'
      }}>
        <Spin size="large" tip={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <Alert
            message={t('common.error.title')}
            description={error}
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={t('public.invitation.success.title')}
            subTitle={t('public.invitation.success.subtitle')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f0f2f5',
      padding: '24px'
    }}>
      <Card
        title={t('public.invitation.title')}
        style={{ maxWidth: '500px', width: '100%' }}
      >
        {invitation && (
          <div style={{ marginBottom: '24px' }}>
            <Alert
              message={
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <ShopOutlined /> <strong>{t('models.warehouse.name')}:</strong> {invitation.warehouse?.name}
                  </div>
                  <div>
                    <MailOutlined /> <strong>{t('public.invitation.fields.email')}:</strong> {invitation.email}
                  </div>
                </div>
              }
              type="info"
            />
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="email"
            label={t('public.invitation.fields.email')}
            tooltip={t('operator.pages.operators.new.email_reference_only')}
          >
            <Input disabled prefix={<MailOutlined />} />
          </Form.Item>

          <Form.Item
            name="username"
            label={t('models.operator.username')}
          >
            <Input disabled prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('public.invitation.fields.name')}
            rules={[
              { required: true, message: t('public.invitation.validation.name_required') }
            ]}
          >
            <Input placeholder={t('public.invitation.placeholders.name')} />
          </Form.Item>

          <Form.Item
            name="password"
            label={t('public.invitation.fields.password')}
            rules={[
              { required: true, message: t('public.invitation.validation.password_required') },
              { min: 6, message: t('public.invitation.validation.password_min_length') }
            ]}
          >
            <Input.Password placeholder={t('public.invitation.placeholders.password')} />
          </Form.Item>

          <Form.Item
            name="password_confirmation"
            label={t('public.invitation.fields.password_confirmation')}
            dependencies={['password']}
            rules={[
              { required: true, message: t('public.invitation.validation.password_confirmation_required') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('public.invitation.validation.password_mismatch')));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('public.invitation.placeholders.password_confirmation')} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
            >
              {t('public.invitation.button.accept')}
            </Button>
          </Form.Item>
        </Form>

        {invitation && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#fafafa', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
            <div>{t('public.invitation.info.expires_at')}: {new Date(invitation.expires_at).toLocaleString()}</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OperatorInvitationAccept;